import { PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { listMachineTypesRequest } from '@/services/machine-types';
import type { MachineTypeListItem } from '@/types/machine-types';
import type { PaginatedResult } from '@/types/roles';

const PAGE_SIZE = 20;

type StatusFilter = 'active' | 'inactive' | 'all';

function isValidStatus(s: string | null): s is StatusFilter {
  return s === 'active' || s === 'inactive' || s === 'all';
}

function truncate(s: string | null, n: number): string {
  if (s === null) return '—';
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

export function MachineTypesListPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: StatusFilter = isValidStatus(statusParam) ? statusParam : 'active';
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [page, setPage] = useState<number>(initialPage);

  // 300ms debounce for search input.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [q]);

  // Sync URL search params when state changes.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== 'active') next.set('status', status);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, page, setSearchParams]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status]);

  const listQuery = useQuery<PaginatedResult<MachineTypeListItem>, ApiError>({
    queryKey: ['machine-types', debouncedQ, status, page, PAGE_SIZE],
    queryFn: () =>
      listMachineTypesRequest({
        q: debouncedQ,
        status,
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 30_000,
  });

  const columns = useMemo<ColumnDef<MachineTypeListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Type Name',
        cell: ({ row }) => (
          <Link
            to={`/machine-types/${String(row.original.id)}`}
            className="text-primary hover:underline"
            data-testid={`machine-types-row-name-${String(row.original.id)}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <span title={row.original.description ?? ''}>
            {truncate(row.original.description, 60)}
          </span>
        ),
      },
      {
        accessorKey: 'customFieldsCount',
        header: 'Custom Fields',
        cell: ({ row }) => <span>{row.original.customFieldsCount}</span>,
      },
      {
        accessorKey: 'machinesCount',
        header: 'Machines',
        cell: ({ row }) =>
          row.original.machinesCount === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span>{row.original.machinesCount}</span>
          ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const data = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tableData = data as unknown as MachineTypeListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('machineTypes.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  const canCreate = permissions.includes('machineTypes.create');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Machine Types</h1>
          <p className="text-muted-foreground">
            Define the schema and metadata for the kinds of machines you operate.
          </p>
        </div>
        {canCreate && (
          <Button asChild data-testid="machine-types-add-button">
            <Link to="/machine-types/new">
              <PlusIcon size={16} weight="bold" /> Add Machine Type
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Machine Types</CardTitle>
          <CardDescription>
            Search by name. Filter by status. Click a row to open its detail view.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-1">
              <Label htmlFor="machine-types-search">Search</Label>
              <Input
                id="machine-types-search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                placeholder="Type name…"
                data-testid="machine-types-search-input"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="machine-types-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isValidStatus(v)) setStatus(v);
                }}
              >
                <SelectTrigger id="machine-types-status" data-testid="machine-types-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" data-testid="machine-types-status-active">
                    Active
                  </SelectItem>
                  <SelectItem value="inactive" data-testid="machine-types-status-inactive">
                    Inactive
                  </SelectItem>
                  <SelectItem value="all" data-testid="machine-types-status-all">
                    All
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {listQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="machine-types-loading">
              Loading machine types…
            </p>
          )}
          {listQuery.isError && (
            <p className="text-sm text-destructive" data-testid="machine-types-error">
              Failed to load machine types.
            </p>
          )}
          {listQuery.data && (
            <>
              <div className="overflow-x-auto">
              <Table data-testid="machine-types-table">
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((h) => (
                        <TableHead key={h.id}>
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-8">
                        <span data-testid="machine-types-empty">No machine types found.</span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-testid={`machine-types-row-${String(row.original.id)}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="machine-types-pagination-summary"
                >
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="machine-types-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="machine-types-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
